import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ListChecks, CheckCircle2, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

// "What's missing" — reads gaps[] from the vault-health response the Dashboard
// already has in state (no new fetch). Additive to the Vault Health card.

const CATEGORY_ORDER = ['assets', 'will', 'people', 'children'];
const CATEGORY_LABELS = { assets: 'Assets', will: 'Will & Legal', people: 'People', children: 'Children' };
const SEVERITY_RANK = { high: 0, medium: 1, low: 2 };

// No red anywhere — low = muted/grey, medium = amber, high = stronger amber.
const SEVERITY_ROW = {
  high: 'border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800',
  medium: 'border-amber-200 bg-amber-50/60 dark:bg-amber-950/10 dark:border-amber-900',
  low: 'border-border bg-muted/30',
};
const SEVERITY_DOT = {
  high: 'bg-amber-500',
  medium: 'bg-amber-400',
  low: 'bg-muted-foreground/40',
};

const DEFAULT_VISIBLE = 5;

const VaultGapsCard = ({ data, loading, error }) => {
  const [expanded, setExpanded] = useState(false);

  // Additive + defensive: the Vault Health card owns the loading/error UI, so this
  // panel simply stays out of the way until there's a real payload to show.
  if (loading || error || !data?.band) return null;

  const gaps = Array.isArray(data.gaps) ? data.gaps : [];

  // Empty state — quiet, positive confirmation (never alarming or blank).
  if (gaps.length === 0) {
    return (
      <Card className="border border-border" data-testid="vault-gaps-card">
        <CardContent className="p-6 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
          <div>
            <h3 className="font-heading font-medium text-foreground">What&apos;s missing</h3>
            <p className="text-sm text-muted-foreground">Your vault looks complete - nothing needs attention right now.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Severity order (high → low), then cap to top N so the panel stays compact.
  const sorted = [...gaps].sort(
    (a, b) => (SEVERITY_RANK[a.severity] ?? 3) - (SEVERITY_RANK[b.severity] ?? 3)
  );
  const visible = expanded ? sorted : sorted.slice(0, DEFAULT_VISIBLE);
  const hiddenCount = sorted.length - visible.length;

  // Group the visible set by category (fixed order); items keep their severity order.
  const grouped = CATEGORY_ORDER
    .map((cat) => ({ cat, items: visible.filter((g) => g.category === cat) }))
    .filter((g) => g.items.length > 0);

  return (
    <Card className="border border-border" data-testid="vault-gaps-card">
      <CardContent className="p-6">
        {/* Header + count */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-medium text-foreground">What&apos;s missing</h3>
          </div>
          <span
            className="text-xs font-medium px-2.5 py-1 rounded-full border bg-primary/10 text-primary border-primary/20"
            data-testid="vault-gaps-count"
          >
            {gaps.length}
          </span>
        </div>

        <div className="mt-4 space-y-4">
          {grouped.map(({ cat, items }) => (
            <div key={cat}>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
                {CATEGORY_LABELS[cat] || cat}
              </p>
              <div className="space-y-2">
                {items.map((gap) => (
                  <div
                    key={gap.id}
                    className={`flex items-start justify-between gap-3 rounded-lg border px-3 py-2.5 ${SEVERITY_ROW[gap.severity] || SEVERITY_ROW.low}`}
                    data-testid="vault-gap-row"
                  >
                    <div className="flex items-start gap-2.5 min-w-0">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 ${SEVERITY_DOT[gap.severity] || SEVERITY_DOT.low}`} />
                      <span className="text-sm text-foreground leading-snug">{gap.message}</span>
                    </div>
                    <Link
                      to={gap.fixLink}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline shrink-0 mt-0.5"
                    >
                      Fix <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {sorted.length > DEFAULT_VISIBLE && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-4 text-xs font-medium text-primary hover:underline"
            data-testid="vault-gaps-toggle"
          >
            {expanded ? 'Show less' : `Show all (${hiddenCount} more)`}
          </button>
        )}
      </CardContent>
    </Card>
  );
};

export default VaultGapsCard;
